import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'

export type ToastVariant = 'info' | 'success' | 'error' | 'warning'

export interface ToastOptions {
  title: string
  description?: string
  variant?: ToastVariant
  /** ms; domyślnie 4500, 0 = nie znika samo */
  duration?: number
}

interface ToastEntry extends Required<Omit<ToastOptions, 'description'>> {
  id: number
  description?: string
}

interface ToastApi {
  push: (options: ToastOptions) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

/** Prosty kontekst powiadomień — bez zależności zewnętrznych. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    ({ title, description, variant = 'info', duration = 4500 }: ToastOptions) => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, title, description, variant, duration }])
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration)
      }
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, description) => push({ title, description, variant: 'success' }),
      error: (title, description) => push({ title, description, variant: 'error', duration: 7000 }),
    }),
    [push, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-[340px] flex-col gap-2">
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

const ACCENT: Record<ToastVariant, string> = {
  info: 'bg-navy-600',
  success: 'bg-success',
  error: 'bg-danger',
  warning: 'bg-warning',
}

function ToastCard({ toast, onDismiss }: { toast: ToastEntry; onDismiss: () => void }) {
  return (
    <div
      role="status"
      className="pointer-events-auto flex overflow-hidden rounded border border-line-soft bg-card shadow-pop"
    >
      <span className={cn('w-[3px] shrink-0', ACCENT[toast.variant])} />
      <div className="min-w-0 flex-1 px-3.5 py-3">
        <p className="text-base font-semibold text-ink">{toast.title}</p>
        {toast.description ? <p className="mt-0.5 text-small text-muted">{toast.description}</p> : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Zamknij powiadomienie"
        className="shrink-0 px-3 text-muted transition-colors duration-fast hover:text-ink"
      >
        ×
      </button>
    </div>
  )
}

/** Hook powiadomień: `const toast = useToast(); toast.success('Zapisano')`. */
export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast musi być użyty wewnątrz <ToastProvider>')
  }
  return context
}
